import { useAtomValue, useSetAtom } from 'jotai';
import React, { useMemo } from 'react';

import { Group, Image, SkeletonRect, Stack,Text } from '@ekhodealer/ekho-common/rev';

import type { FiltersDrawerCheckboxGroupItem } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';
import type { VehicleCategory } from '@ekhodealer/ekho-common';

import { FiltersDrawerCheckboxGroup } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import { updateVehicleCategoryFilterAtom,vehicleCategoryFilterAtom } from '../../../atoms/inventoryAtoms';
import { useInventoryFilterFacetCounts } from '../../../hooks/useInventoryFilterFacetCounts';

import {
	capitalizeWords,
	VEHICLE_CATEGORY_LABELS,
	VEHICLE_CATEGORY_THUMBNAILS,
} from './vehicleCategoryConstants';

const LoadingCheckboxGroup = () => {
	return (
		<Stack itemsSpacing='8'>
			<SkeletonRect width='100%' height='24px' />
			<SkeletonRect width='100%' height='24px' />
			<SkeletonRect width='100%' height='24px' />
			<SkeletonRect width='100%' height='24px' />
		</Stack>
	);
};

/**
 * Creates a styled label for vehicle category with thumbnail image
 */
const createVehicleCategoryLabel = (title: string, imageSrc?: string): React.ReactNode => {
	return (
		<Group itemsAlignX='space-between'>
			<Text size='bodySmall' weight='regular' align='left'>
				{title}
			</Text>
			<div style={{ width: '70px', flexShrink: 0 }}>
				{imageSrc && (
					<Image
						src={imageSrc}
						alt=''
						ariaHidden={true}
						rounding='0'
						style={{ width: '70px', display: 'block' }}
					/>
				)}
			</div>
		</Group>
	);
};

/**
 * VehicleCategorySection component that provides vehicle category filtering options
 * Dynamically generates options based on available categories in inventory with counts
 * Shows thumbnail images alongside category names
 */
export function VehicleCategorySection() {
	const categories = useAtomValue(vehicleCategoryFilterAtom);
	const setCategories = useSetAtom(updateVehicleCategoryFilterAtom);

	// Get dynamic facet counts from the search response
	const { vehicleCategoryCounts, isLoading } = useInventoryFilterFacetCounts();

	// Build category items with dynamic counts from facets
	// Sort by count (descending) then alphabetically for consistent ordering
	const categoryItems: FiltersDrawerCheckboxGroupItem[] = useMemo(() => {
		return Object.entries(vehicleCategoryCounts)
			.map(([category, count]) => {
				const vehicleCategory = category as VehicleCategory;
				const label = VEHICLE_CATEGORY_LABELS[vehicleCategory] || capitalizeWords(category);
				const image = VEHICLE_CATEGORY_THUMBNAILS[vehicleCategory];
				return {
					value: category,
					label: createVehicleCategoryLabel(label, image),
					count,
				};
			})
			.sort((a, b) => {
				// First sort by count (descending)
				if (b.count !== a.count) {
					return b.count - a.count;
				}
				// Then sort alphabetically
				const aLabel = typeof a.label === 'string' ? a.label : '';
				const bLabel = typeof b.label === 'string' ? b.label : '';
				return aLabel.localeCompare(bLabel);
			});
	}, [vehicleCategoryCounts]);

	// Don't render if data has loaded but there are no options
	if (!isLoading && categoryItems.length === 0) {
		return null;
	}

	return (
		<>
			{isLoading ? (
				<LoadingCheckboxGroup />
			) : (
				<FiltersDrawerCheckboxGroup
					label='Category'
					items={categoryItems}
					value={categories}
					onChange={setCategories}
					tooltipHelpText='Category filter counts are based on matched inventory'
				/>
			)}
		</>
	);
}
